import React, { createContext, useContext, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { pipego } from 'api/pipego';
import { whatsappOficialApi } from 'api/whatsappOficialApi';
import { useNotifyAlert } from 'context/NotifyAlert';
import { getWhatsappReleased, getWhatsappUserInfo } from 'util/isAdmin';
import { useQuery, useQueryClient } from 'react-query';
import whatsappUtils from 'api/whatsapp-utils';
import { ACTIVE_CHATS_TAB } from '../utils/whatsappSIdebarTabs';
import { convertChatId } from 'util/utils';

const MessageContext = createContext( {} );

const phoneKey = ( conv ) => conv?.id?.split( '@' )[0]?.replace( /\D/g, '' );
const isFinalized = ( conv ) => conv?.status === 'Finalizado';

// O mesmo telefone pode ter chat no bailey e no oficial, com o mesmo id (telefone@s.whatsapp.net).
// Atualizações por id só valem para a conversa da mesma origem, senão uma apaga a outra.
const isSameConversation = ( conv, id, oficial ) => conv?.id === id && ( conv?.source === 'oficial' ) === !!oficial;

// Uma conversa por telefone na tela, com prioridade para o oficial (AFCO-546) — exceto quando ele
// está finalizado e o do bailey não, para não esconder um atendimento em andamento.
function prioritizeOficialChats( list ) {
	const oficialByPhone = new Map( list.filter( c => c.source === 'oficial' ).map( c => [ phoneKey( c ), c ] ) );
	const baileyByPhone = new Map( list.filter( c => c.source !== 'oficial' ).map( c => [ phoneKey( c ), c ] ) );
	return list.filter( conv => {
		if ( conv.source === 'oficial' ) {
			const bailey = baileyByPhone.get( phoneKey( conv ) );
			return !bailey || !isFinalized( conv ) || isFinalized( bailey );
		}
		const oficial = oficialByPhone.get( phoneKey( conv ) );
		return !oficial || ( isFinalized( oficial ) && !isFinalized( conv ) );
	} );
}

export const MessageProvider = ( { children } ) => {
	const queryClient = useQueryClient();
	const [ warningDialog, setWarningDialog ] = useState( false );

	const { setShowNotify } = useNotifyAlert();

	const [ messages, setMessages ] = useState( [] );
	const [ activeTab, setActiveTab ] = useState( ACTIVE_CHATS_TAB );
	const [ settingsView, setSettingsView ] = useState( null );
	// template aberto em "Configurar template" (Saúde do WhatsApp)
	const [ settingsTemplate, setSettingsTemplate ] = useState( null );
	const [ selectedChat, setSelectedChat ] = useState( null );
	const [ chatListCursor, setChatListCursor ] = useState( null );
	const [ oficialChatListCursor, setOficialChatListCursor ] = useState( null );
	const oficialCursorRef = useRef( null );
	// Estado com os chats do bailey e do oficial; a tela recebe `conversations`, um por telefone.
	const [ allConversations, setConversations ] = useState( [] );
	const conversations = useMemo( () => prioritizeOficialChats( allConversations ), [ allConversations ] );
	const [ caseIdContext, setCaseIdContext ] = useState( null );
	const [ userIdContext, setUserIdContext ] = useState( null );
	const [ userEmailContext, setUserEmailContext ] = useState( null );
	const [ isNewPhoneNumberModalOpen, setIsNewPhoneNumberModalOpen ] = useState( false );
	const [ connectionStatus, setConnectionStatus ] = useState( {
		isAuthenticated : false,
		hasQR           : false,
		isDisconnected  : false
	} );
	const [ templates, setTemplates ] = useState( [] );
	const [ unreadActiveChats, setUnreadActiveChats ] = useState( 0 );
	const [ unreadPendingChats, setUnreadPendingChats ] = useState( 0 );
	const [ loadMoreController, setLoadMoreController ] = useState( false );

	const [ operators, setOperators ] = useState( [] );
	const [ selectedOperators, setSelectedOperators ] = useState( [] );
	const [ selectedUsers, setSelectedUsers ] = useState( [] );
	const [ isLoadingChats, setIsLoadingChats ] = useState( false );
	const [ isRunningReadMessages, setIsRunningReadMessages ] = useState( false );
	const markedReadRef = useRef( new Set() );
	const [ contactNameContext, setContactNameContext ] = useState( null );

	const [ qrCode, setQrCode ] = useState( null );
	// AFCO-417: id da sub-sessão em pareamento, para nunca enviar id vazio nos
	// endpoints destrutivos do bailey-api; e fase visível do pareamento.
	const [ pendingSubSessionId, setPendingSubSessionId ] = useState( null );
	// 'idle' | 'generating' | 'waiting_scan' | 'connecting'
	const [ connectionPhase, setConnectionPhase ] = useState( 'idle' );

	const selectedOperatorsRef = useRef( selectedOperators );
	const messagesCursorRef = useRef( null );
	const shouldScrollToBottomRef = useRef( true );

	const selectedUsersRef = useRef( selectedUsers );
	const [ departments, setDepartments ] = useState( [] );
	const [ selectedDepartments, setSelectedDepartments ] = useState( [] );
	const [ allDepartments, setAllDepartments ] = useState( [] );
	const [ isDepartmentsLoading, setIsDepartmentsLoading ] = useState( true );
	const [ users, setUsers ] = useState( [] );
	const [ allUsers, setAllUsers ] = useState( [] );
	const [ isUsersLoading, setIsUsersLoading ] = useState( true );
	const selectedDepartmentsRef = useRef( selectedDepartments );
	const [ phoneNumbers, setPhoneNumbers ] = useState( [] );
	const [ isPhoneNumbersLoading, setIsPhoneNumbersLoading ] = useState( true );
	const [ subSessionToHandle, setSubSessionToHandle ] = useState( null );
	const [ showOnlyBannedPhones, setShowOnlyBannedPhones ] = useState( false );
	const selectedChatRef = useRef( null );
	const previousChatRef = useRef( null );
	const selectedShowOnlyBannedPhonesRef = useRef( showOnlyBannedPhones );

	useEffect( () => {
		selectedOperatorsRef.current = selectedOperators;
		selectedDepartmentsRef.current = selectedDepartments;
		selectedUsersRef.current = selectedUsers;
		selectedShowOnlyBannedPhonesRef.current = showOnlyBannedPhones;	
	}, [ selectedOperators, selectedDepartments, selectedUsers, showOnlyBannedPhones ] );

	const handleSelectionOperatorsChange = ( selectedOptions ) => {
		setSelectedOperators( selectedOptions );
	};

	const handleSelectionUsersChange = useCallback( ( selectedOptions ) => {
		setSelectedUsers( selectedOptions );
	}, [] );

	const handleSelectionDepartmentsChange = useCallback( ( selectedOptions ) => {
		const operatorsInDepartment = allUsers.filter( operator => selectedOptions.includes( operator.departmentId ) );
		setSelectedDepartments( selectedOptions );
		setOperators( operatorsInDepartment.map( item => ( { ...item, id: item.acordoFechadoId } ) ) );
	}, [ allUsers ] );


	const getTemplates = async ( payload ) => {
		if ( checkWhatsAppStatus() ) {
			const response = await pipego.getTemplates( payload );
			setTemplates( response?.templates );
		}
	};
	useEffect( () => {
		const { activeChats, pendingChats } = conversations.reduce( ( acc, conversation ) => {
			if ( conversation.userId ) {
				if(conversation.status === 'Pendente'){
					acc.activeChats += 1;
				} else if (conversation.unreadCount > 0 && conversation.status !== 'Finalizado'){
						acc.activeChats += 1;
				}
			} else {
				acc.pendingChats += 1;
			}
			return acc;
		}, { activeChats: 0, pendingChats: 0 } );
		setUnreadActiveChats( activeChats );
		setUnreadPendingChats( pendingChats );
	}, [ conversations ] );

	const handleReadMessages = async ( conversation ) => {
		setIsRunningReadMessages( true );
		try {
			if ( conversation?.source === 'oficial' ) {
				const latestInbound = [ ...messages ]
					.reverse()
					.find( msg => msg.source === 'oficial' && msg.type === 'received' );
				if ( latestInbound?.id && !markedReadRef.current.has( latestInbound.id ) ) {
					markedReadRef.current.add( latestInbound.id );
					await whatsappOficialApi.markRead( latestInbound.id, conversation?.tenantId ).catch( () => {} );
					// O backend zera o contador na leitura; zera aqui também para o badge não
					// esperar o próximo polling da lista (15s).
					if ( conversation.unreadCount > 0 ) {
						setConversations( prevConversations =>
							prevConversations.map( conv => ( isSameConversation( conv, conversation.id, true ) ? { ...conv, unreadCount: 0 } : conv ) )
						);
						setSelectedChat( ( prev ) => ( prev?.id === conversation.id ? { ...prev, unreadCount: 0 } : prev ) );
					}
				}
				return;
			}
			if ( conversation.unreadCount > 0 ) {
				await pipego.getMessageHistory( selectedChatRef.current.id, null );
				setConversations( prevConversations =>
					prevConversations.map( conv => {
						if ( isSameConversation( conv, conversation.id, false ) ) {
							setSelectedChat( ( prev ) => ( { ...prev, unreadCount: 0 } ) );
							return { ...conv, unreadCount: 0 };
						}
						return conv;
					} )
				);
			}
		} catch ( error ) {
			console.error( 'Erro ao ler mensagens:', error );
		} finally {
			setIsRunningReadMessages( false );
		}
	};

	const sendTemplateMessage = async ( templateType, callback ) => {
		const templateContent = templates?.find( template => template.template === templateType )?.content;
		if ( templateContent ) {
			try {
				callback( templateContent );
				return;
			} catch ( error ) {
				console.error( 'Erro ao buscar template:', error );
				setShowNotify( {
					status : 'error',
					text   : 'Erro ao buscar template.'
				} );
			}

		}
	};

	const handleSetNewConversationContext = ( caseId, userId, userEmail, contactName ) => {
		console.log( 'handleSetNewConversationContext', contactName );
		setCaseIdContext( caseId );
		setUserIdContext( userId );
		setUserEmailContext( userEmail );
		setContactNameContext( contactName );
	};

	const handleNullifyNewConversationContext = () => {
		console.log( 'handleNullifyNewConversationContext' );
		setCaseIdContext( null );
		setUserIdContext( null );
		setUserEmailContext( null );
		setContactNameContext( null );
	};

	async function handleCreateSession() {
		const isAdminProfileType = getWhatsappUserInfo()?.profile_type_id === 1;
		try {
			if ( getWhatsappReleased() && isAdminProfileType ) {
				setConnectionPhase( 'generating' );
				const response = await pipego.createSession( );
				if ( response?.subSessionId ) {
					setPendingSubSessionId( response.subSessionId );
				}
				setQrCode( response?.qr || null );
				if ( response?.qr ) {
					setConnectionPhase( 'waiting_scan' );
				}
				return response;
			} else {
				setConnectionPhase( 'idle' );
				setShowNotify( {
					status : 'error',
					text   : 'Usuário não autorizado'
				} );
			}
		} catch ( error ) {
			setConnectionPhase( 'idle' );
			console.error( 'Erro ao criar sessão:', error );
			setShowNotify( {
				status : 'error',
				text   : 'Usuário não autorizado'
			} );
		}
	}
	async function checkWhatsAppStatus() {
		try {
			const status = await pipego.getWhatsAppStatus();
			// Atualiza o status de autenticação
			setConnectionStatus( prev => ( {
				...prev,
				isAuthenticated: status.isAuthenticated
			} ) );

			return status.isAuthenticated;
		} catch ( error ) {
			console.error( 'Erro ao verificar status do WhatsApp:', error );
			setConnectionStatus( prev => ( {
				...prev,
				isAuthenticated: false
			} ) );
			return false;
		}
	}

	async function handleRecreateSession() {
		try {
			// AFCO-417: antes isso chamava removeWithPhoneNumber(), que apagava no
			// backend a sub-sessão de telefone vazio — inclusive a que o usuário
			// acabara de conectar. Agora removemos pelo id real e o backend recusa
			// (skipped) se o QR já tiver sido lido.
			if ( pendingSubSessionId ) {
				const result = await pipego.removeQrCodeGeneration( pendingSubSessionId );
				if ( result?.skipped ) {
					setConnectionPhase( 'connecting' );
					return;
				}
				setPendingSubSessionId( null );
			}
			setQrCode( null );
			await handleCreateSession();
			await checkWhatsAppStatus();
		} catch ( error ) {
			console.error( 'Erro ao recriar sessão:', error );
			setShowNotify( {
				status : 'error',
				text   : 'Erro ao recriar sessão'
			} );
		}
	}

	async function handleTogglePhoneNumberStatus( id ) {
		try {
			const response = await pipego.togglePhoneNumberStatus( id );
			if( response?.qr ) {
				setQrCode( response?.qr || null );
				setConnectionPhase( 'waiting_scan' );
				setIsNewPhoneNumberModalOpen( true );
			}
		} catch ( error ) {
			console.error( 'Erro ao alterar status do telefone:', error );
			setShowNotify( {
				status : 'error',
				text   : error?.response?.data?.error || 'Erro ao alterar status do telefone'
			} );
		}
	}


	async function handleTogglePhoneNumberJustReceptive( id ) {
		try {
			const response = await pipego.togglePhoneNumberJustReceptive( id );
			if( response?.message ) {
				setShowNotify( {
					status : 'success',
					text   : response?.message || 'Status de apenas receptivo alterado com sucesso!'
				} );
			}
		} catch ( error ) {
			console.error( 'Erro ao alterar status de apenas receptivo do telefone:', error );
			setShowNotify( {
				status : 'error',
				text   : error?.response?.data?.error || 'Erro ao alterar status de apenas receptivo do telefone'
			} );
		}
	}

	async function handleGetPairingCodeToPhoneCall( id ) {
		try {
			const response = await pipego.getPairingCodeToPhoneCall( id );
			return response?.pairingCode ?? null;
		} catch ( error ) {
			console.error( 'Erro ao obter código de pareamento do whatsapp:', error );
			setShowNotify( {
				status : 'error',
				text   : error?.response?.data?.message || 'Erro ao obter código de pareamento do whatsapp'
			} );
			return null;
		}
	}

	async function handleDisconnectPhoneCall( id ) {
		try {
			const response = await pipego.disconnectPhoneCall( id );
			if( response?.message ) {
				setShowNotify( {
					status : 'success',
					text   : response?.message
				} );
			}
		} catch ( error ) {
			console.error( 'Erro ao disconectar telefone das chamadas', error );
			setShowNotify( {
				status : 'error',
				text   : error?.response?.data?.error || 'Erro ao disconectar telefone das chamadas'
			} );
		}
	}

	// A mesma conversa pode chegar identificada com ou sem o nono dígito do celular
	// (ver convertChatId). A query que alimenta a tela é chaveada pelo id do chat
	// selecionado, então toda escrita no cache precisa resolver para esse mesmo id —
	// senão a atualização vai para uma entrada de cache que a tela nunca lê.
	const resolveChatRef = ( chatId ) => {
		const selectedChatId = selectedChatRef?.current?.id;
		if ( !selectedChatId || !chatId ) {
			return chatId;
		}
		if ( selectedChatId === chatId || convertChatId( selectedChatId ) === chatId ) {
			return selectedChatId;
		}
		return chatId;
	};

	const buildMessagesQueryKey = ( chatId ) => [ 'messages', { chatRef: resolveChatRef( chatId ), loadMoreController } ];

	// O histórico de uma conversa tem duas representações: o estado 'messages', que a
	// tela renderiza, e o cache do react-query, de onde o efeito de messagesData
	// reconstrói esse estado. Escrever em só uma delas faz a outra descartar a
	// alteração no próximo rebuild — por isso toda mutação passa por aqui.
	// alsoOpenChat aplica a transformação também na entrada de cache da conversa aberta.
	// Serve para eventos identificados por id de mensagem cujo remoteJid não é casável
	// com o id do chat (ver onMessageUpdate). Só use com updater por id: para um append
	// isso inseriria a mensagem na conversa errada.
	const applyToChatMessages = ( chatId, updater, { alsoOpenChat = false } = {} ) => {
		const targets = new Set();
		if ( chatId ) {
			targets.add( resolveChatRef( chatId ) );
		}
		if ( alsoOpenChat && selectedChatRef?.current?.id ) {
			targets.add( selectedChatRef.current.id );
		}
		targets.forEach( ( target ) => {
			queryClient.setQueryData( buildMessagesQueryKey( target ), ( oldData ) => oldData && updater( oldData ) );
		} );
		setMessages( ( prev ) => updater( prev ) );
	};

	const upsertMessage = ( list, message ) => [ ...list.filter( msg => msg.id !== message.id ), message ]
		.sort( ( a, b ) => new Date( a.createdAt ) - new Date( b.createdAt ) );

	async function fetchMessages() {
		if ( !selectedChatRef?.current?.id ) return [];

		const isNewChat = previousChatRef?.current?.id !== selectedChatRef?.current?.id;

		if ( isNewChat ) {
			setMessages( [] );
			messagesCursorRef.current = null;
			shouldScrollToBottomRef.current = true;
		}

		try {
			const response = await pipego.getMessageHistory( selectedChat.id, ( messagesCursorRef.current ?? null ) );

			messagesCursorRef.current = response?.cursor;

			const chatFromBackend = response?.chat;
			if ( chatFromBackend ) {
				setSelectedChat( prevChat => {
					if (
						prevChat?.source !== 'oficial' && ( prevChat?.id === chatFromBackend.id ||
						convertChatId( prevChat?.id ) === chatFromBackend.id )
					) {
						return { ...prevChat, ...chatFromBackend };
					}
					return prevChat;
				} );
				setConversations( prevConversations =>
					prevConversations.map( conv =>
						isSameConversation( conv, chatFromBackend.id, false ) ? { ...conv, ...chatFromBackend } : conv
					)
				);
			}

			const messagesResponse = response?.messages?.map( ( msg ) => ( {
				id         : msg.id,
				content    : msg.content,
				createdAt  : msg.createdAt,
				type       : msg.fromMe ? 'sent' : 'received',
				status     : msg.status,
				mediaType  : msg.mediaType,
				storageUrl : msg.storageUrl,
				fileName   : msg.fileName,
				userEmail  : msg.userEmail ?? null,
			} ) );

			if ( isNewChat ) {
				previousChatRef.current = selectedChatRef?.current;
				return messagesResponse;
			}

			previousChatRef.current = selectedChatRef?.current;
			return [ ...messagesResponse, ...messages ].filter( ( msg, index ) =>
				[ ...messagesResponse, ...messages ].findIndex( t => t.id === msg.id ) === index
			);
		} catch ( error ) {
			messagesCursorRef.current = null;
			return [];
		}
	}

	const { data: messagesData, isLoading: isMessagesLoading } = useQuery( {
		queryKey : buildMessagesQueryKey( selectedChatRef?.current?.id ),
		queryFn  : fetchMessages,
		enabled  : !!selectedChatRef?.current?.id,
	} );

	const selectedPhone = selectedChat?.id?.split( '@' )[0] ?? null;

	const { data: oficialMessagesData } = useQuery( {
		queryKey     : [ 'oficial-messages', selectedPhone ],
		queryFn      : async () => {
			if ( !selectedPhone ) return [];
			try {
				const response = await whatsappOficialApi.getMessages( selectedPhone );
				return response?.data?.messages ?? [];
			} catch {
				return [];
			}
		},
		enabled         : !!selectedPhone,
		staleTime       : 0,
		refetchInterval : 5000,
	} );

	const OFICIAL_CHATS_FALLBACK = { chatsOpen: [], chatsFinalized: [], cursor: null };

	const { data: oficialChatsData, refetch: refetchOficialChats } = useQuery( {
		queryKey     : [ 'oficial-chats' ],
		queryFn      : async () => {
			try {
				const response = await whatsappOficialApi.getChats( oficialCursorRef.current, selectedOperatorsRef.current, selectedDepartmentsRef.current );
				return response?.data ?? OFICIAL_CHATS_FALLBACK;
			} catch {
				return OFICIAL_CHATS_FALLBACK;
			}
		},
		staleTime       : 0,
		refetchInterval : 15000,
	} );

	// Reaproveita o cursor já expandido pelo usuário ("Carregar Mais") — não reseta a paginação.
	function loadMoreOficialFinalized() {
		oficialCursorRef.current = oficialChatListCursor;
		return refetchOficialChats();
	}

	// Usado ao trocar filtros de operador/departamento — reinicia a paginação oficial do zero.
	function resetOficialCursor() {
		oficialCursorRef.current = null;
		return refetchOficialChats();
	}

	const { data: tenantConfigData } = useQuery( {
		queryKey : [ 'tenant-config' ],
		queryFn  : async () => {
			try {
				const response = await whatsappOficialApi.getTenantConfig();
				return response?.data ?? { hasMetaApi: false };
			} catch {
				return { hasMetaApi: false };
			}
		},
		staleTime : 5 * 60 * 1000,
	} );

	const hasMetaApi = tenantConfigData?.hasMetaApi ?? false;

	useEffect( () => {
		if ( !oficialChatsData ) return;

		setOficialChatListCursor( oficialChatsData.cursor ?? null );

		const combinedOficialChats = [
			...( oficialChatsData.chatsOpen ?? [] ),
			...( oficialChatsData.chatsFinalized ?? [] ),
		];

		const mapOficialChatToConversation = ( c ) => {
			const ts = c.conversationTimestamp
				? ( () => { const d = new Date( c.conversationTimestamp ); d.setHours( d.getHours() - 3 ); return d.getTime(); } )()
				: new Date().toISOString();
			return {
				id                  : `${c.phoneNumber}@s.whatsapp.net`,
				name                : c.contactName || c.phoneNumber,
				caseId              : c.caseId ?? null,
				userId              : c.userId ?? null,
				userEmail           : c.userEmail ?? null,
				userName            : c.userName ?? null,
				departmentName      : c.departmentName ?? null,
				status              : c.status ?? 'Em aberto',
				content                   : c.lastMessageText ?? null,
				mediaType                 : c.lastMessageMediaType ?? 'text',
				lastMessageFromMe         : c.lastMessageFromMe ?? false,
				lastMessageReactionEmoji  : c.lastMessageReactionEmoji ?? null,
				lastMessageStatus         : c.lastMessageStatus ?? null,
				processNumber             : c.processNumber ?? null,
				createdAt                 : ts,
				unreadCount               : c.unreadCount ?? 0,
				source                    : 'oficial',
				phoneNumberIsActive       : true,
				officialPhoneNumber       : c.officialPhoneNumber ?? null,
				tenantId                  : c.tenantId ?? null,
			};
		};

		// Guarda todos os oficiais, mesmo com telefone que o bailey também tem: quem aparece na
		// tela é decidido em `prioritizeOficialChats`.
		setConversations( prev => [
			...prev.filter( c => c.source !== 'oficial' ),
			...combinedOficialChats.map( mapOficialChatToConversation ),
		] );

		// Chats oficiais não têm websocket — sem isso, o chat aberto na tela (selectedChat) nunca
		// refletiria uma mudança de status/caseId/operador feita por outro atendente ou pelo backend.
		setSelectedChat( prev => {
			if ( !prev || prev.source !== 'oficial' ) return prev;
			const prevPhone = prev.id?.split( '@' )[0]?.replace( /\D/g, '' );
			const updated = combinedOficialChats.find( c => c.phoneNumber?.replace( /\D/g, '' ) === prevPhone );
			if ( !updated ) return prev;
			// Nome e departamento vêm sempre da lista (mesma linha do responsável): sem `?? prev`, para
			// não sobrar o nome do responsável anterior depois de uma troca.
			return { ...prev, status: updated.status ?? prev.status, caseId: updated.caseId ?? prev.caseId, userId: updated.userId ?? prev.userId, userEmail: updated.userEmail ?? prev.userEmail, userName: updated.userName ?? null, departmentName: updated.departmentName ?? null };
		} );
	}, [ oficialChatsData ] ); // eslint-disable-line

	useEffect( () => {
		if ( messagesData != null ) {
			const oficialMessages = ( oficialMessagesData ?? [] ).map( msg => {
				const d = new Date( msg.createdAt );
				d.setHours( d.getHours() - 3 );
				return {
					id            : msg.id,
					content       : msg.text,
					createdAt     : d.getTime(),
					type          : msg.fromMe ? 'sent' : 'received',
					status        : msg.status,
					mediaType     : msg.mediaType,
					storageUrl    : msg.storageUrl ?? null,
					fileName      : msg.fileName ?? null,
					userEmail     : msg.fromMe ? ( msg.senderEmail ?? null ) : null,
					source        : 'oficial',
					errorMessage  : msg.errorMessage ?? null,
					reactionEmoji : msg.reactionEmoji ?? null,
				};
			} );

			// Oficiais primeiro: com o mesmo id, fica a versão do servidor (status real), não a provisória do envio.
			const allMessages = [ ...oficialMessages, ...messagesData ];
			const uniqueMessages = allMessages.filter( ( msg, index ) =>
				allMessages.findIndex( t => t.id === msg.id ) === index
			);

			const sortedMessages = uniqueMessages.sort( ( a, b ) => new Date( a.createdAt ) - new Date( b.createdAt ) );

			setMessages( sortedMessages );
		}
	}, [ messagesData, oficialMessagesData ] );

	async function handleSendMessage( e, form, userEmail ) {
		if ( !selectedChat?.id ) {
			return;
		}

		const invalidText = !e.message || e.message.trim() === '';

		if ( !e.mediaType && invalidText ) {
			return;
		}

		const formattedText = !e.message ? '' : e.message;
		const messageDate = new Date();
		const idMessageToSend = new Date().getTime();
		const messageToSend = {
			id           : idMessageToSend,
			content      : formattedText,
			mediaType    : e?.mediaType ?? null,
			mediaContent : e.content,
			storageUrl   : e?.storageUrl ?? null,
			fileName     : e?.fileName ?? 'Documento',
			mimetype     : e?.mimetype ?? null,
			duration     : e?.duration ?? null,
			createdAt    : messageDate.setHours( messageDate.getHours() - 3 ),
			type         : 'sent',
			status       : 1,
			userEmail    : userEmail ?? null
		};
		form.reset( { 'message': '' } );

		try {
			applyToChatMessages( selectedChatRef?.current?.id, ( list ) => upsertMessage( list, messageToSend ) );

			if ( selectedChat?.source === 'oficial' ) {
				const phone = selectedChat.id.split( '@' )[0];
				try {
					const response = await whatsappOficialApi.sendText( {
						to       : phone,
						body     : formattedText,
						tenantId : selectedChat.tenantId,
					} );
					// Troca o id provisório pelo do backend (o mesmo que o GET /v1/messages devolve),
					// senão a mensagem aparece duas vezes quando o polling traz a versão do servidor.
					if ( response?.data?.id ) {
						applyToChatMessages( selectedChatRef?.current?.id, ( list ) => list.map( msg =>
							msg.id === idMessageToSend ? { ...msg, id: response.data.id } : msg
						) );
					}
				} catch ( err ) {
					const errText = err?.response?.data?.error || 'Erro ao enviar mensagem';
					applyToChatMessages( selectedChatRef?.current?.id, ( list ) => list.map( msg =>
						msg.id === messageToSend.id ? { ...msg, status: 0, errorMessage: errText } : msg
					) );
					setShowNotify( { status: 'error', text: errText } );
				}
				handleNullifyNewConversationContext();
				return;
			}

			const messageResponse = await pipego.sendMessage(
				selectedChat?.id,
				{ text: messageToSend.content, mediaType: e.mediaType, mediaContent: e.content, fileName: e?.fileName, mentions: [], fromTemplate : e?.fromTemplate, duration: e?.duration },
				caseIdContext,
				userIdContext,
				userEmailContext,
				contactNameContext
			);
			if( messageResponse?.key?.id ) {
				const messageTimestamp = new Date( Number( messageResponse?.messageTimestamp ) * 1000 );
				const dataToUpdate = {
					id        : messageResponse?.key?.id,
					createdAt : messageTimestamp.setHours( messageTimestamp.getHours() - 3 )
				};
				// A mensagem é do chat aberto por definição. O remoteJid da resposta pode vir
				// em outra forma (nono dígito, @lid) e apontar para outra entrada de cache.
				applyToChatMessages( selectedChatRef?.current?.id, ( list ) => list
					.map( msg => msg.id === idMessageToSend ? { ...msg, ...dataToUpdate } : msg )
					.sort( ( a, b ) => new Date( a.createdAt ) - new Date( b.createdAt ) ) );
			}
			if ( selectedChatRef?.current?.status === 'Pendente' ) {
				const payload = {
					...selectedChatRef?.current,
					status: 'Em aberto'
				};
				setSelectedChat( {
					...payload
				} );

				updateChat( payload );
			}
			handleNullifyNewConversationContext();
			return;
		} catch ( error ) {
			console.log( error );
			if ( error?.response?.data?.showAlert ) {
				const baseUrl = `${window.location.href.split( 'app' )[0]}app/casos`;
				const caseUrl = caseIdContext ? `${baseUrl}/${caseIdContext.slice( 2 )}` : baseUrl;
				const caseDetailsUrl = baseUrl;

				const subTextWithLinks = (
					<span>
						Você pode enviar mensagens pela API OFICIAL, acessando a tela do {' '}
						<a
							href={caseUrl}
							target="_blank"
							rel="noopener noreferrer"
							style={{ color: '#1976d2', textDecoration: 'underline' }}
						>
							caso
						</a>
						, ou em lote pela tela{' '}
						<a
							href={caseDetailsUrl}
							target="_blank"
							rel="noopener noreferrer"
							style={{ color: '#1976d2', textDecoration: 'underline' }}
						>
							 detalhes do caso
						</a>
					</span>
				);

				setWarningDialog( {
					title   : error.response.data.message,
					subText : subTextWithLinks,
					open    : true
				} );
			} else {
				setShowNotify( {
					// O back pode informar o tipo do aviso (ex.: 'warning' para número apenas receptivo).
					// Por padrão, qualquer erro é exibido em vermelho.
					status : error?.response?.data?.alertType || 'error',
					text   : error?.response?.data?.message || 'Erro ao enviar mensagem'
				} );
				handleNullifyNewConversationContext();
				applyToChatMessages( selectedChatRef?.current?.id, ( list ) => list.map( msg =>
					msg.id === messageToSend.id ? { ...msg, status: 0 } : msg
				) );
			}
		}
	}

	async function loadChats( ignoreCursor = false ) {
		setIsLoadingChats( true );
		try {
			const pipegoResult = await pipego.getOperatorChats( ignoreCursor ? null : chatListCursor, selectedOperatorsRef.current, selectedDepartmentsRef.current, selectedShowOnlyBannedPhonesRef.current )
				.then( v => ( { status: 'fulfilled', value: v } ) )
				.catch( e => ( { status: 'rejected', reason: e } ) );

			const pipegoData = pipegoResult.status === 'fulfilled' ? pipegoResult.value : null;

			setChatListCursor( pipegoData?.cursor ?? null );

			const pipegoChats = pipegoData
				? [ ...( pipegoData.chatsWithNegotiator ?? [] ), ...( pipegoData.chatsWithoutNegotiator ?? [] ), ...( pipegoData.chatsWithNegotiatorFinalized ?? [] ) ]
				: [];

			// Chats oficiais são gerenciados exclusivamente pelo useQuery (refetchInterval: 15s).
			// Preserva todos os que já estão em state; quem aparece por telefone é decidido em
			// `prioritizeOficialChats`.
			setConversations( prev => [ ...pipegoChats, ...prev.filter( c => c.source === 'oficial' ) ] );

			setSelectedChat( prev => {
				// Chat oficial aberto é atualizado só pela lista oficial: o do bailey tem o mesmo id.
				if ( !prev || prev.source === 'oficial' ) return prev;
				const allChats = [ ...pipegoChats ];
				const found = allChats.find( chat => chat?.id === prev?.id || chat?.id === convertChatId( prev?.id ) );
				return found ? { ...prev, ...found } : prev;
			} );

			if ( pipegoResult.status === 'fulfilled' ) {
				setQrCode( null );
				setUnreadActiveChats( parseInt( pipegoData?.activeUnreadCount ?? 0 ) );
				setUnreadPendingChats( parseInt( pipegoData?.pendingUnreadCount ?? 0 ) );
			} else if ( pipegoResult.reason?.response?.data?.qrCode ) {
				setQrCode( pipegoResult.reason.response.data.qrCode );
			}

			return pipegoChats;
		} catch ( error ) {
			console.error( 'Erro ao carregar conversas:', error );
		} finally {
			setIsLoadingChats( false );
		}
	}

	async function updateChat( payload ) {
		try {
			const existing = conversations.find( c => c.id === payload.id );
			const isOficial = ( payload.source ?? existing?.source ) === 'oficial';

			if ( isOficial ) {
				const phone = payload.id?.split( '@' )[0];
				// isFavorite: chats_oficial não tem essa coluna — não é persistido, só refletido localmente.
				if ( payload.status !== undefined || payload.caseId !== undefined ) {
					await whatsappOficialApi.updateChat( phone, { status: payload.status, caseId: payload.caseId } );
				}
				setConversations( prev => prev.map( conv => isSameConversation( conv, payload.id, true ) ? { ...conv, ...payload } : conv ) );
				setSelectedChat( prev => prev?.id === payload.id ? { ...prev, ...payload } : prev );
				return { data: { ...existing, ...payload } };
			}

			const response = await pipego.updateChat( payload );
			setConversations( prevConversations => {
				return prevConversations.map( conv => {
					if ( isSameConversation( conv, payload.id, false ) ) {
						return { ...conv, ...payload };
					}
					return conv;
				} );
			} );
			return response;
		} catch ( error ) {
			throw new Error( error?.response?.data?.error || 'Erro ao atualizar conversa' );
		}

	}

	async function assignChat( jid, userId, userEmail, caseId ) {
		try {
			if ( caseId?.length < 7 ) {
				return setShowNotify( {
					status : 'error',
					text   : 'Digite um id de caso válido ( pelo menos 7 caractéres )'
				} );
			}

			const isOficial = selectedChatRef.current?.source === 'oficial';

			if ( isOficial ) {
				const phone = jid.split( '@' )[0];
				const response = await whatsappOficialApi.updateOperator( phone, userId );
				if ( caseId ) {
					await whatsappOficialApi.updateChat( phone, { caseId } );
				}
				const newStatus = response?.data?.status;
				const update = {
					userId, userEmail, operatorEmail: userEmail,
					...( caseId ? { caseId } : {} ),
					...( newStatus !== undefined ? { status: newStatus } : {} ),
				};
				setSelectedChat( prev => prev?.id === jid ? { ...prev, ...update } : prev );
				setConversations( prev => prev.map( c => isSameConversation( c, jid, true ) ? { ...c, ...update } : c ) );
				return setShowNotify( {
					status : 'success',
					text   : 'Atendimento atribuído com sucesso!'
				} );
			}

			const response = await pipego.updateOperator( jid, userId, userEmail, caseId );

			if ( response.status === 200 ) {
				const chatId = response.data?.id ?? jid;
				const update = response.data?.id
					? response.data
					: { userId, userEmail, operatorEmail: userEmail };
				setSelectedChat( prev => prev?.id === chatId ? { ...prev, ...update } : prev );
				setConversations( prev => prev.map( c => isSameConversation( c, chatId, false ) ? { ...c, ...update } : c ) );
				return setShowNotify( {
					status : 'success',
					text   : 'Atendimento atribuído com sucesso!'
				} );
			}
		} catch ( error ) {
			console.log( error );
			return setShowNotify( {
				status : 'error',
				text   : error?.response?.data?.error || 'Algo deu errado.'
			} );
		}
	}

	async function closeChat( jid ) {
		try {
			const isOficial = selectedChatRef.current?.source === 'oficial';

			const response = isOficial
				? await whatsappOficialApi.closeChatService( jid.split( '@' )[0] )
				: await pipego.closeChatService( jid );

			if ( response.status === 200 ) {
				if ( isOficial ) {
					// Sem websocket para chats oficiais — atualização otimista local;
					// o polling de 15s (oficial-chats) reforça o valor depois.
					setConversations( prev => prev.map( c => isSameConversation( c, jid, true ) ? { ...c, status: 'Finalizado' } : c ) );
					setSelectedChat( prev => prev?.id === jid ? { ...prev, status: 'Finalizado' } : prev );
				}
				return setShowNotify( {
					status : 'success',
					text   : 'Atendimento finalizado com sucesso!'
				} );
			}

			return setShowNotify( {
				status : 'error',
				text   : 'Erro interno.'
			} );
		} catch ( error ) {
			console.log( error );
			return setShowNotify( {
				status : 'error',
				text   : 'Algo deu errado.'
			} );
		}
	}

	function statusHandler( status ) {
		setMessages( prev => prev.map( msg =>
			msg.id === status.messageId ? { ...msg, status: status.ack } : msg
		) );
	}

	async function handleChatClick( conversation ) {
		if ( conversation.isNewChat ) {
			handleSetNewConversationContext( conversation.caseId, conversation.userId, conversation.userEmail, conversation.contactName );
		}

		const userEmail = getWhatsappUserInfo()?.email;

		const userCanChangeStatus = conversation.userEmail === userEmail || !conversation.userEmail;

		const isNewChat = previousChatRef?.current?.id !== conversation.id;
		
		if ( conversation.status === 'Pendente' && userCanChangeStatus && isNewChat ) {
			const payload = {
				...conversation,
				operatorEmail : conversation?.userEmail || 'Não atribuído',
				status        : 'Em aberto'
			};
			setSelectedChat( {
				...payload
			} );
			if ( !conversation.isNewChat ) {
				updateChat( payload );
			}
		} else {
			setSelectedChat( {
				...conversation,
				operatorEmail: conversation?.userEmail || 'Não atribuído',
			} );
		}

		selectedChatRef.current = conversation;
	}

	async function onMessageReceived( messageInfo ) {
		const message = messageInfo?.data?.data.message;
		const isMessageFromSelectedChat = message?.remoteJid === selectedChatRef.current?.id || message?.remoteJid === convertChatId( selectedChatRef.current?.id );
		if ( isMessageFromSelectedChat ) {
			setSelectedChat( ( prev ) => ( { ...prev, unreadCount: ( prev.unreadCount ?? 0 ) + 1 } ) );
		}
		const queryKey = buildMessagesQueryKey( message?.remoteJid );
		const oldData = await queryClient.getQueryData( queryKey );
		if ( oldData ) {
			// O filtro anterior comparava o índice do array não ordenado com o findIndex de uma
			// cópia ordenada — índices de listas diferentes, o que descartava mensagens válidas.
			queryClient.setQueryData( queryKey, ( oldData ) => {
				const merged = [ ...oldData, message ];
				return merged
					.filter( ( msg, index ) => merged.findIndex( t => t.id === msg.id ) === index )
					.sort( ( a, b ) => new Date( a.createdAt ) - new Date( b.createdAt ) );
			} );
		}
		setConversations( prevConversations =>
			prevConversations.map( conv => {
				if ( isSameConversation( conv, message?.remoteJid, false ) ) {
					return {
						...conv,
						content   : message?.content,
						mediaType : message?.mediaType,
						fromMe    : message?.fromMe
					};
				}
				return conv;
			} ).sort( ( a, b ) => new Date( a.createdAt ) - new Date( b.createdAt ) )
		);
	}

	async function onMessageSend( messageInfo ) {
		const message = messageInfo?.data?.data.message;
		const isForSelectedChat = selectedChatRef?.current?.id && (
			selectedChatRef.current.id === message?.remoteJid ||
			convertChatId( selectedChatRef.current.id ) === message?.remoteJid
		);
		const queryKey = buildMessagesQueryKey( message?.remoteJid );
		const oldData = await queryClient.getQueryData( queryKey );
		if ( oldData ) {
			queryClient.setQueryData( queryKey, ( oldData ) => oldData
				.map( msg => msg.id === message.id ? { ...msg, ...message } : msg )
				.sort( ( a, b ) => new Date( a.createdAt ) - new Date( b.createdAt ) ) );
		}

		if ( isForSelectedChat ) {
			setMessages( prev => prev.map( msg => msg.id === message.id ? { ...msg, status: message.status } : msg ) );
		}

		setConversations( ( prev ) => prev.map( conv => {
			if ( isSameConversation( conv, message?.remoteJid, false ) ) {
				return {
					...conv,
					content   : message?.content,
					mediaType : message?.mediaType,
					fromMe    : message?.fromMe,
					createdAt : message?.createdAt || conv.createdAt
				};
			}
			return conv;
		} ).sort( ( a, b ) => new Date( a.createdAt ) - new Date( b.createdAt ) ) );
		if( message?.mediaType === 'call'  || message?.mediaType === 'template') {
			setConversations( prevConversations => {
						return prevConversations.map( conv => isSameConversation( conv, message?.remoteJid, false ) ?
							{ ...conv, 
							content: message?.content, 
							mediaType: message?.mediaType, 
							fromMe: message?.fromMe, 
							createdAt: message?.createdAt 
						} : conv );
				} );
			if(oldData) {
				queryClient.setQueryData( queryKey, ( oldData ) =>{
					const exists = oldData.some( msg => msg.id === message.id );
					if( exists ) {
						return oldData
							.map( msg => msg.id === message.id ? { ...msg, status: message.status } : msg )
							.sort( ( a, b ) => new Date( a.createdAt ) - new Date( b.createdAt ) );
					}
					return [ ...oldData, message ].sort( ( a, b ) => new Date( a.createdAt ) - new Date( b.createdAt ) ) ;
				} )
			}
			if(selectedChatRef?.current?.id && (selectedChatRef?.current?.id === message?.remoteJid || convertChatId( selectedChatRef?.current?.id ) === message?.remoteJid)){
				setMessages( prevMessages => {
					const messageAlreadyExists = prevMessages.find( msg => msg.id === message.id );
					if( messageAlreadyExists ) {
						return prevMessages
							.map( msg => msg.id === message.id ? { ...msg, status: message.status } : msg )
							.sort( ( a, b ) => new Date( a.createdAt ) - new Date( b.createdAt ) );
					}
					return [ ...prevMessages, message ].sort( ( a, b ) => new Date( a.createdAt ) - new Date( b.createdAt ) ) ;
				} );
			}
		}
	}
	async function onMessageUpdate( messageInfo ) {
		const updates = messageInfo?.data?.data?.updates;
		if ( !updates?.length ) return;

		for ( const update of updates ) {
			const messageId = update?.key?.id;
			const newStatus = update?.update?.status;
			const remoteJid = update?.key?.remoteJid;
			if ( !messageId || newStatus === undefined ) continue;

			// O ack de status chega endereçado por lid (ex.: 26040554500119@lid), que o front
			// não tem como casar com o id do chat, que é o jid de telefone. Como a atualização
			// é por id de mensagem, aplicamos também na conversa aberta: é inofensivo quando a
			// mensagem não está lá, e essa é a única entrada de cache que a tela lê. Sem isso o
			// status subia só no estado e voltava atrás na reconstrução seguinte.
			applyToChatMessages( remoteJid, ( list ) => list.map( msg =>
				msg.id === messageId ? { ...msg, status: newStatus } : msg
			), { alsoOpenChat: true } );
		}
	}

	// conteudo da mensagem não é atualizado nesse evento
	async function onChatUpdate( chatInfo ) {
		const chat = chatInfo?.data?.data?.chat;
		if ( chat ) {
			setConversations( prevConversations => {
				return prevConversations.map( conv => {
					if ( isSameConversation( conv, chat?.id, false ) ) {
						return { ...conv, ...chat };
					}
					return conv;
				} ).sort( ( a, b ) => new Date( a.createdAt ) - new Date( b.createdAt ) ) ;
			} );
			setSelectedChat( prevChat => {
				if ( prevChat?.source !== 'oficial' && ( prevChat?.id === chat?.id || convertChatId( prevChat?.id ) === chat?.id ) ) {
					return { ...prevChat, ...chat };
				}
				return prevChat;
			} );
		}
	}

	async function onChatsReload(chatInfo) {
		if(getWhatsappUserInfo()?.profile_type_id === 1 && !selectedShowOnlyBannedPhonesRef.current){
			const chat = chatInfo?.data?.data?.chat;
			if ( chat ) {
				if(selectedOperatorsRef.current.length > 0 || selectedDepartmentsRef.current.length > 0){
					const chatFromSelectedOperator = selectedOperatorsRef.current.includes( chat.userId ) ;
					const chatFromSelectedDepartments = selectedDepartmentsRef.current.includes( chat.departmentId );
					if(!chatFromSelectedOperator && !chatFromSelectedDepartments){
						setConversations( prevConversations => {
							const conversationAlreadyExists = prevConversations.find( conv => isSameConversation( conv, chat?.id, false ) );
							if( conversationAlreadyExists ) {
								return prevConversations.filter( conv => !isSameConversation( conv, chat?.id, false ) )
							}
							return prevConversations
						} );
						setSelectedChat( prevChat => {
							if ( prevChat?.source !== 'oficial' && prevChat?.id === chat?.id ) {
								return null;
							}
							return prevChat;
						} );
						return;
					}
				}
				
				setConversations( prevConversations => {
					const conversationAlreadyExists = prevConversations.find( conv => isSameConversation( conv, chat?.id, false ) );
					if( conversationAlreadyExists ) {
						return prevConversations.map( conv => {
							if ( isSameConversation( conv, chat?.id, false ) ) {
								return { ...conv, ...chat };
							}
							return conv;
						} ).sort( ( a, b ) => new Date( a.createdAt ) - new Date( b.createdAt ) ) ;
					}
					return [ ...prevConversations, chat ].sort( ( a, b ) => new Date( a.createdAt ) - new Date( b.createdAt ) ) ;
				} );
				setSelectedChat( prevChat => {
					if ( prevChat?.source !== 'oficial' && ( prevChat?.id === chat?.id || convertChatId( prevChat?.id ) === chat?.id ) ) {
						return { ...prevChat, ...chat };
					}
					return prevChat;
				} );
				return;
			}
		}
		await loadChats();
	}

	async function onMessagingHistorySet( history ) {
		console.log( 'onMessagingHistorySet: ', history );
		await loadChats();
	}

	async function onSubSessionUpdate( subSession ) {
		console.log( 'onSubSessionUpdate: ', subSession );
		const subSessionUpdated = subSession?.data?.data;
		// AFCO-417: o payload do socket é parcial (id, isActive, phoneNumber,
		// isJustReceptive). Uma sub-sessão ainda desconhecida não pode ser inserida
		// aqui sem os demais campos, então recarregamos a lista inteira.
		let isKnownSubSession = true;
		setPhoneNumbers( ( prev ) => {
			isKnownSubSession = prev.some( phone => phone.id === subSessionUpdated?.id );
			if ( !isKnownSubSession ) {
				return prev;
			}
			const updatedPhoneNumbers = prev.map( phone => phone.id === subSessionUpdated.id ? { ...phone, ...subSessionUpdated } : phone );
			const haveAnyPhoneNumberActive = updatedPhoneNumbers.some( phone => phone.isActive );
			if( !haveAnyPhoneNumberActive ) {
				setConnectionStatus(prev => ({
					...prev,
					isAuthenticated: false
				}));
			}
			return updatedPhoneNumbers.filter( phone => !phone.isHidden );
		} );
		if ( !isKnownSubSession ) {
			await handleFetchPhoneNumbers();
			return;
		}
		setIsPhoneNumbersLoading( false );
	}

	const addDepartment = async ( newDepartment ) => {
		const departmentAlreadyExists = allDepartments.find( department => department.id === newDepartment.id );
		if( departmentAlreadyExists ) {
			setAllDepartments( ( prev ) => prev
				.map( department => department.id === newDepartment.id ? newDepartment : department )
				.sort( ( a, b ) => a.name.localeCompare( b.name ) ) );
			setDepartments( ( prev ) => prev
				.map( department => department.id === newDepartment.id ? newDepartment : department )
				.sort( ( a, b ) => a.name.localeCompare( b.name ) ) );
			return;
		}
		handleFetchUsersAndOperators();
		handleFetchDepartments();
	};

	const handleFetchDepartments = async () => {
		const [ users, departments ] = await Promise.all( [
			whatsappUtils.listUsers(),
			whatsappUtils.listDepartments()
		] );
		const departmentsWithUsers = departments.map( department => {
			const usersInDepartment = users.filter( user => user.departmentId === department.id );
			return { ...department, users: usersInDepartment };
		} );
		setAllDepartments( departmentsWithUsers );
		setDepartments( departmentsWithUsers );
	};

	const handleFetchPhoneNumbers = async () => {
		try {
			const phoneNumbers = await pipego.getPhoneNumbers();
			setPhoneNumbers( ( phoneNumbers ?? [] ).filter( phone => !phone.isHidden ) );
		} catch ( error ) {
			// Backend retorna 404 quando não há sub-sessões cadastradas — trata como lista vazia.
			if ( error?.response?.status === 404 ) {
				setPhoneNumbers( [] );
			} else {
				console.error( 'Erro ao buscar telefones:', error );
			}
		} finally {
			setIsPhoneNumbersLoading( false );
		}
	};


	useEffect( () => {
		setIsDepartmentsLoading( true );
		handleFetchDepartments();
		setIsDepartmentsLoading( false );
		handleFetchPhoneNumbers();
	}, [] );

	const addUser = async ( newUser ) => {
		handleFetchDepartments();
		const userAlreadyExists = allUsers.find( user => user.email === newUser.email );
		if( userAlreadyExists ) {
			setAllUsers( ( prev ) => prev
				.map( user => user.id === newUser.id ? newUser : user )
				.sort( ( a, b ) => a.name.localeCompare( b.name ) ) );
			setUsers( ( prev ) => prev
				.map( user => user.id === newUser.id ? newUser : user )
				.sort( ( a, b ) => a.name.localeCompare( b.name ) ) );
			return;
		}
		setAllUsers( ( prev ) => [ ...prev, newUser ].sort( ( a, b ) => a.name.localeCompare( b.name ) ) );
		setUsers( ( prev ) => [ ...prev, newUser ].sort( ( a, b ) => a.name.localeCompare( b.name ) ) );
	};

	const handleDeactivateUser = async ( id ) => {
		try {
			await whatsappUtils.deactivateUser( id );
			handleFetchUsersAndOperators();
			handleFetchDepartments();
			loadChats();
			setShowNotify( {
				status : 'success',
				text   : 'Usuário desativado com sucesso!'
			} );
		} catch ( error ) {
			console.error( 'Erro ao desativar usuário:', error );
			setShowNotify( {
				status : 'error',
				text   : 'Erro ao desativar usuário'
			} );
		}
	};

	const handleFetchUsersAndOperators = async () => {
		setIsUsersLoading( true );
		const response = await whatsappUtils.listUsers();
		setOperators( response.map( item => ( { ...item, id: item.acordoFechadoId  } ) ) );
		setAllUsers( response );
		setUsers( response );
		setIsUsersLoading( false );
	};

	useEffect( () => {
		handleFetchUsersAndOperators();
	}, [] );


	return (
		<MessageContext.Provider
			value={{
				messages,
				activeTab,
				selectedChat,
				conversations,
				connectionStatus,
				qrCode,
				pendingSubSessionId,
				setPendingSubSessionId,
				connectionPhase,
				setConnectionPhase,
				setMessages,
				setActiveTab,
				settingsView,
				setSettingsView,
				settingsTemplate,
				setSettingsTemplate,
				setConversations,
				setConnectionStatus,
				setQrCode,
				fetchMessages,
				handleSendMessage,
				loadChats,
				statusHandler,
				handleChatClick,
				checkWhatsAppStatus,
				// onReceivedMessage,
				messagesCursor: messagesCursorRef.current,
				chatListCursor,
				oficialChatListCursor,
				loadMoreOficialFinalized,
				resetOficialCursor,
				closeChat,
				assignChat,
				handleCreateSession,
				handleRecreateSession,
				updateChat,
				templates,
				getTemplates,
				sendTemplateMessage,
				unreadActiveChats,
				unreadPendingChats,
				isMessagesLoading,
				setLoadMoreController,
				shouldScrollToBottomRef,
				operators,
				selectedOperators,
				handleSelectionOperatorsChange,
				handleSelectionDepartmentsChange,
				isLoadingChats,
				setChatListCursor,
				handleReadMessages,
				isRunningReadMessages,
				selectedChatRef,
				departments,
				allDepartments,
				isDepartmentsLoading,
				addDepartment,
				setDepartments,
				users,
				allUsers,
				isUsersLoading,
				addUser,
				setUsers,
				selectedDepartments,
				warningDialog,
				setWarningDialog,
				setSelectedDepartments,
				selectedUsers,
				handleSelectionUsersChange,
				onChatUpdate,
				onMessageReceived,
				onMessageSend,
				onMessageUpdate,
				onChatsReload,
				handleDeactivateUser,
				setSelectedChat,
				phoneNumbers,
				isPhoneNumbersLoading,
				isNewPhoneNumberModalOpen,
				setIsNewPhoneNumberModalOpen,
				handleFetchPhoneNumbers,
				handleTogglePhoneNumberStatus,
				handleTogglePhoneNumberJustReceptive,
				handleGetPairingCodeToPhoneCall,
				handleDisconnectPhoneCall,
				onMessagingHistorySet,
				onSubSessionUpdate,
				subSessionToHandle,
				setSubSessionToHandle,
				showOnlyBannedPhones,
				setShowOnlyBannedPhones,
				hasMetaApi
			}}
		>
			{children}
		</MessageContext.Provider>
	);
};

export const useMessage = () => {
	const context = useContext( MessageContext );
	if ( !context ) {
		throw new Error( 'useMessage must be used within a MessageProvider' );
	}
	return context;
};
